import { useEffect, useState } from "react";
import { apiFetch } from "../api/client.ts";
import { Layout } from "../components/layout.tsx";

type HealthData = { status: string; timestamp: string; version?: string };

function StatusBadge({ status }: { status: "ok" | "error" | "loading" }) {
  if (status === "loading") return <span className="text-xs text-gray-500">Checking...</span>;
  if (status === "ok")
    return <span className="rounded bg-green-900 px-2 py-0.5 text-xs text-green-200">Healthy</span>;
  return <span className="rounded bg-red-900 px-2 py-0.5 text-xs text-red-200">Unreachable</span>;
}

export function AdminSystemHealthPage() {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [status, setStatus] = useState<"ok" | "error" | "loading">("loading");
  const [checkedAt, setCheckedAt] = useState<string | null>(null);

  async function check() {
    setStatus("loading");
    try {
      const res = (await apiFetch("/api/v1/health")) as HealthData;
      setHealth(res);
      setStatus(res.status === "ok" ? "ok" : "error");
      setCheckedAt(new Date().toISOString());
    } catch {
      setStatus("error");
      setCheckedAt(new Date().toISOString());
    }
  }

  useEffect(() => {
    check();
  }, []);

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">System Health</h1>
          <button
            onClick={check}
            className="rounded bg-gray-700 px-4 py-2 text-sm text-white hover:bg-gray-600"
          >
            Refresh
          </button>
        </div>

        {checkedAt && (
          <p className="text-sm text-gray-500">
            Last checked: {new Date(checkedAt).toLocaleTimeString()}
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-medium text-white">API Worker</span>
              <StatusBadge status={status} />
            </div>
            {health && (
              <div className="space-y-1 text-sm text-gray-400">
                <div>
                  Status: <span className="text-white">{health.status}</span>
                </div>
                {health.version && (
                  <div>
                    Version: <span className="text-white">{health.version}</span>
                  </div>
                )}
                {health.timestamp && (
                  <div>
                    Server time:{" "}
                    <span className="text-white">
                      {new Date(health.timestamp).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
            <div className="mb-3 font-medium text-white">Platform Info</div>
            <div className="space-y-1 text-sm text-gray-400">
              <div>
                Runtime: <span className="text-white">Cloudflare Workers</span>
              </div>
              <div>
                Database: <span className="text-white">Cloudflare D1</span>
              </div>
              <div>
                Storage: <span className="text-white">Cloudflare R2</span>
              </div>
              <div>
                Queue: <span className="text-white">Cloudflare Queues</span>
              </div>
              <div>
                Version: <span className="text-white">AIOS v1.0</span>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
          <h2 className="mb-3 text-lg font-semibold text-white">AI Departments Status</h2>
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
            {[
              "Sales & CRM",
              "Marketing & Lead Gen",
              "Knowledge & RAG",
              "Project Delivery",
              "AI Operations",
              "Finance & Billing",
              "Customer Support",
              "Enterprise Platform",
              "Analytics",
            ].map((dept) => (
              <div key={dept} className="flex items-center gap-2">
                <div className="h-2 w-2 shrink-0 rounded-full bg-green-400" />
                <span className="text-gray-300">{dept}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
